/**
 * `effectsModule: EngineEffectsModule` (W1-08 formats-legacy-respack; contract core/engine/types.ts): the
 * engine's seam for extra.json shader effects and background videos. W1-05b's engine calls `effects` and `videos`
 * once per frame (R§8 order); evaluation follows the extra.json keyframe rules (extraAnim.ts: clamp only for a
 * non-default slice, no linear shortcut, extrapolation) with effects active on [start, end) (effectsEval.ts) and
 * the video timing and attach quirk (videos.ts).
 *
 * Compiled tables are cached in `ctx.effectsStore` per IrExtraJson object (`ctx.irExtra()` returns a new object
 * whenever effects or videos change), so a frame only evaluates.
 *
 * Package assets: custom shaders ("/path") and video files are package paths, but draws carry asset ids; they are
 * mapped with the context's `assetIdForPath` (contract, CR-W1-08-1). An unknown path draws nothing (built-in
 * shaders always draw).
 */

import type { Sec } from '../../time/types';
import type { IrExtraJson } from '../../ir/types';
import type { EngineCompileContext, EngineEffectsModule, FrameDrawListBuilder, FrameOptions } from '../types';
import { compileEffects, extraTempo, fillEffects, type AssetResolver, type CompiledEffect } from './effectsEval';
import { compileVideos, fillVideos, type CompiledVideo } from './videos';

export type { AssetResolver } from './effectsEval';

/** `ctx.effectsStore` key of the compiled tables. */
const COMPILED = 'effects.compiled';

function resolverOf(ctx: Pick<EngineCompileContext, 'assetIdForPath'>): AssetResolver {
  return (path) => ctx.assetIdForPath(path);
}

interface Compiled {
  readonly src: IrExtraJson;
  readonly effects: readonly CompiledEffect[];
  readonly videos: readonly CompiledVideo[];
}

/** The compiled tables of the current extra.json (null when the chart has none). */
export function compiledExtra(ctx: Pick<EngineCompileContext, 'irExtra' | 'effectsStore'>): Compiled | null {
  const extra = ctx.irExtra();
  if (extra === null) {
    ctx.effectsStore.delete(COMPILED);
    return null;
  }
  const cached = ctx.effectsStore.get(COMPILED) as Compiled | undefined;
  if (cached?.src === extra) return cached;
  const tempo = extraTempo(extra);
  const compiled: Compiled = { src: extra, effects: compileEffects(extra, tempo), videos: compileVideos(extra, tempo) };
  ctx.effectsStore.set(COMPILED, compiled);
  return compiled;
}

export const effectsModule: EngineEffectsModule = {
  effects(ctx: EngineCompileContext, t: Sec, opts: FrameOptions, out: FrameDrawListBuilder): void {
    out.effects.length = 0;
    out.globalEffects.length = 0;
    if (!opts.showEffects) return;
    const compiled = compiledExtra(ctx);
    if (compiled === null) return;
    fillEffects(compiled.effects, t, resolverOf(ctx), out);
  },
  videos(ctx: EngineCompileContext, t: Sec, opts: FrameOptions, out: FrameDrawListBuilder): void {
    out.videos.length = 0;
    if (!opts.showVideos) return;
    const compiled = compiledExtra(ctx);
    if (compiled === null) return;
    fillVideos(ctx, compiled.videos, t, opts, resolverOf(ctx), out);
  },
};
